import * as React from 'react';

import moment from 'moment';
import { Dispatch } from 'redux';
import { connect } from 'react-redux';

import { Translate } from 'react-localize-redux';

import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';

// Types
import SimpleTrack from '@common/types/release/simple-track';

// SDK
import { hasPlanFeature } from '@webapp/sdk/plans';

// Types
import { PlanNames } from '@services/product/types/plan-names';

// Store
import AppState from '@webapp/store/state';
import { UpdateField } from '@webapp/store/release/action-creators';
import { trackList } from '@webapp/store/tracks/selectors';
import { UpdateTrackField } from '@webapp/store/tracks/action-creators';

// Components
import Checkbox from '@components/form/checkbox';
import Datepicker from '@components/form/datepicker';
import PreOrderShopIcons from '@components/basics/icons/preorder-icons';

interface PreorderReleaseCtrlProps {
  className?: string;
  isPreorder: boolean;
  date?: string;
  releaseDate?: string;
  preorderDate?: string;
  isNewRelease: boolean;
  hasPreorder: boolean;
  isPlanFeature: boolean;
  isAppleSelected: boolean;
  tracks: Partial<SimpleTrack>[];
  updatePreorderDate: (date: string | undefined) => UpdateField<'preorderDate'>;
  updateHasPreorder: (value: boolean) => UpdateField<'hasPreorder'>;
  updateTracksInstantGratification: (
    trackId: string
  ) => UpdateTrackField<'isInstantGratification'>;
}

const mapStateToProps = (state: AppState) => {
  const tracks = trackList(state.tracks.tracks, state.release.release?.id);
  return {
    isPreorder: !!state.release.release?.preorderDate,
    date: state.release.release?.preorderDate,
    releaseDate: state.release.release?.releaseDate,
    preorderDate: state.release.release?.preorderDate,
    isNewRelease: state.release.release?.releaseHistory === 'new',
    hasPreorder: !!state.release.release?.hasPreorder,
    isPlanFeature: !!(
      state.subscription.current?.plan.toUpperCase().includes('PRO') &&
      state.subscription.current.isActive
    ),
    tracks,
    isAppleSelected: !!(
      (!state.release.release?.stores?.length || // all stores are selected
        state.release.release.stores.some((c) =>
          c.name.startsWith('Apple Music')
        )) && // apple is selected
      (hasPlanFeature(
        state.subscription.current?.plan.toLocaleLowerCase() as PlanNames,
        'RELEASE.APPLE_DISTRIBUTION'
      ) ||
        state.release.release?.hasAppleMusic)
    ),
  };
};

const mapDispatchToProps = (dispatch: Dispatch) => ({
  updatePreorderDate: (date: string | undefined) =>
    dispatch(new UpdateField<'preorderDate'>('preorderDate', date)),
  updateHasPreorder: (value: boolean) =>
    dispatch(new UpdateField<'hasPreorder'>('hasPreorder', value)),
  updateTracksInstantGratification: (trackId: string) =>
    dispatch(new UpdateTrackField(trackId, 'isInstantGratification', false)),
});

const getMinDate = () => {
  return moment().startOf('day').add(1, 'day');
};

const getMaxDate = (releaseDate?: string) => {
  let date;

  if (releaseDate) {
    date = releaseDate;
  }
  return moment(date).endOf('day').subtract(7, 'days');
};

export const PreorderReleaseCtrl: React.FC<PreorderReleaseCtrlProps> = ({
  releaseDate,
  preorderDate,
  isPlanFeature,
  updatePreorderDate,
  updateHasPreorder,
  ...props
}) => {
  const [showPreOrder, setShowPreOrder] = React.useState(false);

  React.useEffect(() => {
    const clearPreOrder = () => {
      updatePreorderDate(undefined);
      updateHasPreorder(false);
    };

    if (!releaseDate) {
      setShowPreOrder(false);
      clearPreOrder();
    } else {
      const today = moment().startOf('day');
      const maxDate = getMaxDate(releaseDate);
      const minDate = getMinDate();

      const diff = maxDate.diff(minDate, 'days');
      const maxIsSameOrAfter = moment(maxDate).isSameOrAfter(today);

      const condition = maxIsSameOrAfter && diff > 0;
      setShowPreOrder(condition);

      if (!condition) {
        clearPreOrder();
      }

      const invalidPreorderDate = moment(preorderDate)?.isAfter(maxDate);
      if (invalidPreorderDate) {
        updatePreorderDate(undefined);
      }
    }
  }, [releaseDate, preorderDate, updateHasPreorder, updatePreorderDate]);

  const toggleDate = () => {
    const newVal = !props.hasPreorder;
    updateHasPreorder(newVal);
    if (!newVal && props.tracks) {
      props.tracks.forEach(
        (track) => track.id && props.updateTracksInstantGratification(track.id)
      );
    }
  };

  const item = {
    visible: { opacity: 1, y: 0 },
    hidden: { opacity: 0, y: -50 },
  };

  if (!(showPreOrder && props.isAppleSelected && props.isNewRelease)) {
    return null;
  }

  return (
    <div className={props.className}>
      <Checkbox
        active={props.hasPreorder}
        disabled={!isPlanFeature}
        onClick={() => toggleDate()}
        title={<Translate id="RELEASE.DATES_STORES.PREORDER.TITLE" />}
        text={<Translate id="RELEASE.DATES_STORES.PREORDER.TEXT" />}
        customContent={<PreOrderShopIcons />}
        customBorderRadius={props.hasPreorder ? '4px 4px 0 0' : undefined}
      />
      <AnimatePresence exitBeforeEnter>
        {props.hasPreorder ? (
          <DatepickerMotion variants={item}>
            <Datepicker
              title={
                <Translate id="RELEASE.DATES_STORES.PREORDER.INPUT.LABEL" />
              }
              value={props.date}
              min={getMinDate()}
              max={getMaxDate(releaseDate)}
              customBorderRadius="0 0 4px 4px"
              onChange={(value) => updatePreorderDate(value)}
            />
            {}
          </DatepickerMotion>
        ) : null}
      </AnimatePresence>
    </div>
  );
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(PreorderReleaseCtrl);

const DatepickerMotion = styled(motion.div)`
  position: relative;
  margin-top: 3px;
`;
